<template>
  <div class="page-container">
    <div class="settings-form">
      <div class="header-section">
        <h2 class="page-title">TX广告账号管理</h2>

        <div class="license-info">
          <template v-if="hasSmartCardZt">
            <div class="license-item">
              <span class="label">总授权数：</span>
              <span class="value">{{ licenseInfo.total === -1 ? '无限制' : licenseInfo.total }}</span>
            </div>
            <div class="license-item">
              <span class="label">已使用：</span>
              <span class="value">{{ licenseInfo.used }}</span>
            </div>
            <div class="license-item">
              <span class="label">剩余可用：</span>
              <span class="value">{{ licenseInfo.remaining === -1 ? '无限制' : licenseInfo.remaining }}</span>
            </div>
          </template>
          <template v-else>
            <div class="license-item">
              <span class="label">当前模式：</span>
              <span class="value">单主体模式</span>
            </div>
            <div class="license-item">
              <span class="label">总主体数：</span>
              <span class="value">1</span>
            </div>
          </template>
        </div>
      </div>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <el-input v-model="searchForm.ghz_name" placeholder="请输入TX广告名称" clearable size="large"
          style="width: 300px; margin-right: 15px" @keyup.enter="handleSearch">
          <template #prefix>
            <el-icon>
              <Search />
            </el-icon>
          </template>
        </el-input>

        <el-button type="primary" size="large" @click="handleSearch">
          <el-icon>
            <Search />
          </el-icon>搜索
        </el-button>

        <el-button type="success" size="large" @click="openAddDialog" style="float: right">
          <el-icon>
            <Plus />
          </el-icon>新增TX广告
        </el-button>

        <el-button type="info" size="large" @click="batchCheckAuthorization" :loading="batchCheckLoading"
          style="margin-left: 15px">
          <el-icon>
            <Refresh />
          </el-icon>一键检测RPA授权
        </el-button>

        <el-button type="primary" size="large" @click="openOnlineAuthDialog" class="online-auth-btn">
          <el-icon>
            <Key />
          </el-icon>在线授权
        </el-button>
      </div>

      <!-- 账号列表 -->
      <el-table :data="accountList" stripe style="width: 100%; margin-top: 20px" v-loading="loading" row-key="id"
        :row-class-name="getRowClassName">
        <el-table-column prop="ghz_name" label="TX广告名称" width="180" />

        <el-table-column label="关联用户" width="150">
          <template #default="{ row }">
            <div class="user-info">
              <div class="user-nickname">{{ getUserInfo(row.user_id).nickname }}</div>
              <div class="user-phone">{{ getUserInfo(row.user_id).phone }}</div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="授权类型" width="100">
          <template #default="{ row }">
            <el-tag :type="row.oauth_type === 'ACCOUNT_TYPE_WECHAT' ? 'success' : 'primary'" size="small">
              {{ row.oauth_type === 'ACCOUNT_TYPE_WECHAT' ? '微信' : 'QQ' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="授权状态" width="100">
          <template #default="{ row }">
            <el-tag :type="row.auth_status === 1 ? 'success' : 'danger'" size="small">
              {{ row.auth_status === 1 ? '正常' : '失效' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Token有效期" width="140">
          <template #default="{ row }">
            <div class="token-expire-info">
              <div>剩余: {{ formatExpireTime(row.expire_in_seconds) }}</div>
              <div class="refresh-time">刷新: {{ formatTime(row.last_refresh_time) || '-' }}</div>
            </div>
          </template>
        </el-table-column>

        <el-table-column prop="access_token" label="Access_Token" width="200">
          <template #default="{ row }">
            <span v-if="row.access_token">{{ row.access_token.substring(0, 20) }}...</span>
            <span v-else class="empty-value">未设置</span>
          </template>
        </el-table-column>

        <el-table-column label="Refresh_Token" width="150">
          <template #default="{ row }">
            <span v-if="row.refresh_token">******</span>
            <span v-else class="empty-value">未设置</span>
          </template>
        </el-table-column>

        <el-table-column label="RPA授权" width="220">
          <template #default="{ row }">
            <div class="rpa-auth-buttons">
              <el-button v-if="!row.cookies || row.cookies === 'null' || row.cookies === '[]'" type="primary"
                size="small" @click="startAuthorization(row)">
                点击授权
              </el-button>
              <el-button v-else type="warning" size="small" @click="startAuthorization(row)">
                重新授权
              </el-button>
              <el-button :disabled="!row.cookies || row.cookies === 'null' || row.cookies === '[]'" type="success"
                size="small" @click="checkAuthorization(row)" :loading="checkLoading[row.id]">
                检测授权
              </el-button>
            </div>
          </template>
        </el-table-column>

        <!-- 令牌授权列 -->
        <el-table-column label="令牌授权" width="140" fixed="right">
          <template #default="{ row }">
            <div class="token-auth-status">
              <el-tooltip
                v-if="row.user_status === 2 && row.user_token && row.user_token_expire_time && !isTokenExpired(row.user_token_expire_time)"
                effect="light" placement="left"
                :content="`令牌有效期至：${formatTokenExpireTime(row.user_token_expire_time)}`">
                <div class="token-auth-success">
                  <el-icon color="#67C23A" :size="16">
                    <Check />
                  </el-icon>
                  <span class="token-status-text">已授权</span>
                </div>
              </el-tooltip>
              <el-tooltip v-else-if="row.user_status === 2 && isTokenExpired(row.user_token_expire_time)" effect="light"
                placement="left" :content="`令牌已过期，点击重新授权`">
                <el-button type="warning" size="small" @click="startTokenAuth(row)" class="token-auth-btn">
                  <el-icon>
                    <Refresh />
                  </el-icon>重新授权
                </el-button>
              </el-tooltip>
              <el-tooltip v-else-if="row.user_status === 1" effect="light" placement="left"
                content="员工账号未完成实名认证，点击重新授权">
                <el-button type="warning" size="small" @click="startTokenAuth(row)" class="token-auth-btn">
                  <el-icon>
                    <User />
                  </el-icon>重新授权
                </el-button>
              </el-tooltip>
              <el-tooltip v-else-if="row.user_status === 0" effect="light" placement="left"
                content="未找到腾讯广告员工账号，点击重新授权">
                <el-button type="warning" size="small" @click="startTokenAuth(row)" class="token-auth-btn">
                  <el-icon>
                    <Search />
                  </el-icon>重新授权
                </el-button>
              </el-tooltip>
              <el-button v-else type="primary" size="small" @click="startTokenAuth(row)" class="token-auth-btn">
                <el-icon>
                  <Key />
                </el-icon>令牌授权
              </el-button>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="账号ID" width="120">
          <template #default="{ row }">
            <span>{{ row.account_id || '-' }}</span>
          </template>
        </el-table-column>

        <el-table-column label="IP+端口" width="150">
          <template #default="{ row }">
            <span v-if="row.ip_port">{{ row.ip_port }}</span>
            <span v-else class="empty-value">未设置</span>
          </template>
        </el-table-column>

        <el-table-column label="账号密码" width="180">
          <template #default="{ row }">
            <span v-if="row.ip_password" class="password-preview">{{ row.ip_password }}</span>
            <span v-else class="empty-value">未设置</span>
          </template>
        </el-table-column>

        <el-table-column prop="notes" label="备注" width="150" />
        <el-table-column prop="create_time" label="创建时间" width="180" />
        <el-table-column prop="update_time" label="更新时间" width="180" />

        <el-table-column label="独立指纹" width="100" fixed="right">
          <template #default="{ row }">
            <div class="fingerprint-status">
              <el-tooltip effect="light" placement="left" popper-class="fingerprint-tooltip"
                :disabled="!row.fingerprint || row.fingerprint === 'null' || row.fingerprint === '[]'">
                <template #content>
                  <div v-if="row.fingerprint && row.fingerprint !== 'null' && row.fingerprint !== '[]'">
                    <div class="fingerprint-detail">
                      <h4>浏览器指纹详情</h4>
                      <div class="fingerprint-item" v-for="(value, key) in parseFingerprint(row.fingerprint)"
                        :key="key">
                        <span class="fingerprint-label">{{ formatLabel(key) }}:</span>
                        <span class="fingerprint-value">{{ value }}</span>
                      </div>
                    </div>
                  </div>
                </template>
                <el-icon v-if="row.fingerprint && row.fingerprint !== 'null' && row.fingerprint !== '[]'"
                  color="#67C23A" :size="20" class="success-icon">
                  <Check />
                </el-icon>
                <el-icon v-else color="#F56C6C" :size="20" class="error-icon">
                  <Close />
                </el-icon>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="操作" width="280" fixed="right">
          <template #default="scope">
            <el-button type="primary" size="small" @click="openEditDialog(scope.row)">
              <el-icon>
                <Edit />
              </el-icon>编辑
            </el-button>
            <el-button type="danger" size="small" @click="handleDelete(scope.row.id)">
              <el-icon>
                <Delete />
              </el-icon>删除
            </el-button>
            <el-button type="info" size="small" @click="refreshToken(scope.row)"
              :loading="refreshLoading[scope.row.id]">
              <el-icon>
                <Refresh />
              </el-icon>刷新
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination v-model:current-page="pagination.page" v-model:page-size="pagination.pageSize"
          :total="pagination.total" :page-sizes="[10, 20, 30, 50]" layout="total, sizes, prev, pager, next, jumper"
          @size-change="fetchAccountList" @current-change="fetchAccountList" />
      </div>
    </div>

    <!-- 在线授权对话框 -->
    <el-dialog v-model="onlineAuthDialogVisible" title="TX广告在线授权" width="680px" class="online-auth-dialog-wrapper">
      <div class="online-auth-dialog">
        <div class="auth-type-section">
          <h3 class="section-title">选择授权方式</h3>
          <div class="auth-type-buttons">
            <el-radio-group v-model="authType">
              <el-radio label="ACCOUNT_TYPE_WECHAT" size="large">
                <div class="auth-type-item wechat-item">
                  <div class="auth-icon-wrapper">
                    <div class="auth-icon wechat-icon">💬</div>
                    <div class="selected-indicator">
                      <el-icon>
                        <Check />
                      </el-icon>
                    </div>
                  </div>
                  <div class="auth-text">
                    <div class="auth-title">微信登录</div>
                    <div class="auth-desc">推荐使用，授权流程更快捷</div>
                    <div class="auth-features">
                      <span class="feature-tag">快速</span>
                      <span class="feature-tag">安全</span>
                      <span class="feature-tag">稳定</span>
                    </div>
                  </div>
                </div>
              </el-radio>
              <el-radio label="ACCOUNT_TYPE_QQ" size="large">
                <div class="auth-type-item qq-item">
                  <div class="auth-icon-wrapper">
                    <div class="auth-icon qq-icon">🐧</div>
                    <div class="selected-indicator">
                      <el-icon>
                        <Check />
                      </el-icon>
                    </div>
                  </div>
                  <div class="auth-text">
                    <div class="auth-title">QQ登录</div>
                    <div class="auth-desc">使用QQ账号进行授权</div>
                    <div class="auth-features">
                      <span class="feature-tag">兼容</span>
                      <span class="feature-tag">通用</span>
                    </div>
                  </div>
                </div>
              </el-radio>
            </el-radio-group>
          </div>
        </div>

        <div class="auth-process-section">
          <h3 class="section-title">授权流程</h3>
          <div class="process-steps">
            <div class="process-step">
              <div class="step-number">1</div>
              <div class="step-content">
                <div class="step-title">选择授权方式</div>
                <div class="step-desc">选择微信或QQ登录</div>
              </div>
            </div>
            <div class="step-arrow">→</div>
            <div class="process-step">
              <div class="step-number">2</div>
              <div class="step-content">
                <div class="step-title">扫码登录</div>
                <div class="step-desc">在新窗口完成扫码授权</div>
              </div>
            </div>
            <div class="step-arrow">→</div>
            <div class="process-step">
              <div class="step-number">3</div>
              <div class="step-content">
                <div class="step-title">自动完成</div>
                <div class="step-desc">系统自动保存账号信息</div>
              </div>
            </div>
          </div>
        </div>

        <div class="tips-section">
          <div class="tips-header"><el-icon class="tips-icon">
              <InfoFilled />
            </el-icon><span>温馨提示</span></div>
          <div class="tips-content">
            <p>• 授权过程安全加密，不会保存您的账号密码</p>
            <p>• 建议使用微信授权，流程更快捷稳定</p>
            <p>• 授权成功后系统将自动创建广告账号</p>
            <p>• 可随时在腾讯广告平台撤销授权</p>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="onlineAuthDialogVisible = false" size="large" class="cancel-btn">取消授权</el-button>
          <el-button type="primary" @click="startOnlineAuth" :loading="authLoading" size="large" class="confirm-btn">
            <template #loading><el-icon class="is-loading">
                <Loading />
              </el-icon>授权中...</template>
            <template #default><el-icon>
                <Key />
              </el-icon>开始授权</template>
          </el-button>
        </div>
      </template>
    </el-dialog>

    <!-- QR Code Dialog -->
    <el-dialog v-model="qrDialogVisible" title="TX广告授权" width="400px" :close-on-click-modal="false">
      <div class="qr-code-container">
        <div v-if="qrLoading" class="loading-container">
          <el-icon class="is-loading" size="30">
            <Loading />
          </el-icon>
          <span>正在获取二维码...</span>
        </div>
        <img v-else :src="qrCodeBase64" alt="TX广告授权二维码" class="qr-code-image" />
        <div class="qr-instructions">
          <p>请使用微信扫描上方二维码授权TX广告</p>
          <p>扫描成功后点击下方确认按钮完成授权</p>
        </div>
      </div>
      <template #footer>
        <el-button @click="qrDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmAuthorization" :loading="confirmLoading"
          :disabled="!qrCodeBase64">确认授权</el-button>
      </template>
    </el-dialog>

    <!-- 新增/编辑对话框 -->
    <el-dialog v-model="dialogVisible" :title="dialogTitle" width="600px" :close-on-click-modal="false">
      <el-form ref="accountForm" :model="accountForm" label-width="120px" :rules="formRules">
        <el-form-item label="广告名称" prop="ghz_name">
          <el-input v-model="accountForm.ghz_name" placeholder="请输入TX广告名称" size="large" />
        </el-form-item>
        <el-form-item label="授权类型">
          <el-select v-model="accountForm.oauth_type" placeholder="请选择授权类型" size="large">
            <el-option label="微信登录" value="ACCOUNT_TYPE_WECHAT" />
            <el-option label="QQ登录" value="ACCOUNT_TYPE_QQ" />
          </el-select>
        </el-form-item>
        <el-form-item label="Access_Token" prop="access_token">
          <el-input v-model="accountForm.access_token" placeholder="请输入TX广告Access_Token" size="large" />
        </el-form-item>
        <el-form-item label="Refresh_Token" prop="refresh_token">
          <el-input v-model="accountForm.refresh_token" placeholder="请输入TX广告Refresh_Token" size="large" show-password />
        </el-form-item>
        <el-form-item label="账号ID">
          <el-input v-model="accountForm.account_id" placeholder="请输入账号ID（可选）" size="large" />
        </el-form-item>
        <el-form-item label="IP+端口">
          <el-input v-model="accountForm.ip_port" placeholder="格式：232.33.1.1:16817（可选）" size="large" />
          <div class="form-tip">非必填项，可留空</div>
        </el-form-item>
        <el-form-item label="账号密码">
          <el-input v-model="accountForm.ip_password" placeholder="格式：账号:密码，如：34123:55332（可选）" size="large"
            show-password />
          <div class="form-tip">非必填项，可留空</div>
        </el-form-item>
        <el-form-item label="备注信息">
          <el-input v-model="accountForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="submitAccountForm">确认</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import {
  Search, Plus, Edit, Delete, Loading, Refresh, Check, Close, Key, User, InfoFilled
} from '@element-plus/icons-vue'

export default {
  name: 'AccountManagement',
  components: { Search, Plus, Edit, Delete, Loading, Refresh, Check, Close, Key, User, InfoFilled },
  data() {
    const validateIpPort = (rule, value, callback) => {
      if (value && !/^(\d{1,3}\.){3}\d{1,3}:\d{1,5}$/.test(value)) {
        callback(new Error('格式错误，示例：192.168.1.1:8080'));
      } else { callback(); }
    };
    const validateAccountPassword = (rule, value, callback) => {
      if (value && !/^[^:]+:[^:]+$/.test(value)) {
        callback(new Error('格式错误，示例：username:password'));
      } else { callback(); }
    };
    return {
      loading: false,
      refreshLoading: {},
      checkLoading: {},
      batchCheckLoading: false,
      dialogVisible: false,
      dialogTitle: '新增TX广告',
      isEditMode: false,
      qrDialogVisible: false,
      qrLoading: false,
      confirmLoading: false,
      qrCodeBase64: '',
      currentAuthRow: null,
      userList: [],
      userMap: {},
      onlineAuthDialogVisible: false,
      authType: 'ACCOUNT_TYPE_WECHAT',
      authLoading: false,
      searchForm: { ghz_name: '' },
      accountForm: {
        id: null,
        ghz_name: '',
        ip_port: '',
        ip_password: '',
        access_token: '',
        refresh_token: '',
        notes: '',
        oauth_type: 'ACCOUNT_TYPE_WECHAT',
        account_id: ''
      },
      formRules: {
        ghz_name: [{ required: true, message: '请输入TX广告名称', trigger: 'blur' }],
        access_token: [{ required: true, message: '请输入TX广告AppID', trigger: 'blur' }],
        refresh_token: [{ required: true, message: '请输入TX广告Secret', trigger: 'blur' }],
        ip_port: [{ validator: validateIpPort, trigger: 'blur' }],
        ip_password: [{ validator: validateAccountPassword, trigger: 'blur' }]
      },
      accountList: [],
      pagination: { page: 1, pageSize: 10, total: 0 },
      hasSmartCardZt: false,
      licenseInfo: { total: 0, used: 0, remaining: 0 }
    }
  },
  computed: {
    userInfo() { return JSON.parse(Cookies.get('user_info') || '{}') }
  },
  mounted() {
    this.fetchAccountList();
    this.fetchUserList();
    this.fetchLicenseInfo();
    this.checkSmartCardZt();
    this.setupMessageListener();
    this.handleAuthCallback();
  },
  methods: {
    getRowClassName({ row }) { return `account-row-${row.id}`; },
    formatTime(timestamp) { if (!timestamp) return '-'; return timestamp; },
    formatExpireTime(seconds) {
      if (!seconds || seconds <= 0) return '已过期';
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      return hours > 0 ? `${hours}小时${minutes}分钟` : `${minutes}分钟`;
    },
    formatTokenExpireTime(timestamp) {
      if (!timestamp) return '未知';
      const date = new Date(timestamp * 1000);
      return date.toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' });
    },
    isTokenExpired(expireTime) { if (!expireTime) return true; return expireTime < Math.floor(Date.now() / 1000); },
    parseFingerprint(fingerprint) { try { return typeof fingerprint === 'string' ? JSON.parse(fingerprint) : fingerprint; } catch (e) { return {}; } },
    formatLabel(key) {
      const map = { 'user_agent': '用户代理', 'screen_resolution': '屏幕分辨率', 'language': '语言', 'webgl_vendor': 'WebGL供应商', 'renderer': '渲染器', 'timezone': '时区', 'hardware_concurrency': '硬件并发数', 'device_memory': '设备内存', 'platform': '平台', 'do_not_track': '禁止追踪' };
      return map[key] || key;
    },
    setupMessageListener() {
      window.addEventListener('message', (event) => {
        const allowedOrigins = ['https://hd.aijzdi.com', 'https://mp.aijzdi.com', window.location.origin];
        if (!allowedOrigins.includes(event.origin)) return;
        const message = event.data;
        if (message.type === 'OAUTH_SUCCESS') {
          this.$message.success('授权成功，正在刷新页面...');
          setTimeout(() => this.fetchAccountList(), 1000);
        } else if (message.type === 'TOKEN_AUTH_CALLBACK') {
          this.processTokenAuthCallback(message);
        }
      });
    },
    async processTokenAuthCallback(authData) {
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_management.php`, {
          action: 'handle_oauth_callback', store_id: this.userInfo.id, state: authData.state,
          user_status: authData.user_status, user_token: authData.user_token, expire_time: authData.expire_time
        });
        if (response.data.success) {
          const msgs = { 0: '未找到腾讯广告员工账号', 1: '员工账号未完成实名认证', 2: '令牌授权成功' };
          const types = { 0: 'warning', 1: 'warning', 2: 'success' };
          this.$message({ message: msgs[authData.user_status], type: types[authData.user_status] });
          this.fetchAccountList();
          if (authData.user_status === 2) {
            this.$notify({ title: '令牌授权完成', message: `授权成功！令牌有效期至：${this.formatTokenExpireTime(authData.expire_time)}`, type: 'success', duration: 5000 });
          }
        } else { this.$message.error(response.data.error || '令牌授权处理失败'); }
      } catch (error) { console.error('处理令牌授权回调失败:', error); this.$message.error('处理令牌授权回调失败'); }
    },
    startTokenAuth(row) {
      const redirectUri = encodeURIComponent(`${config.BASE_URL}/shop/oauth_callback.html`);
      const state = row.id.toString();
      const authUrl = `https://ad.qq.com/account-center/single/user-authorize?redirect_uri=${redirectUri}&state=${state}`;
      const authWindow = window.open(authUrl, 'tencent_token_auth', 'width=800,height=700');
      if (!authWindow) { this.$message.error('请允许弹出窗口以进行授权'); return; }
      this.$message.success('已打开授权页面，请在新窗口中完成授权');
    },
    handleAuthCallback() {
      const urlParams = new URLSearchParams(window.location.search);
      const state = urlParams.get('state'), userStatus = urlParams.get('user_status'), userToken = urlParams.get('user_token'), expireTime = urlParams.get('expire_time');
      if (state && userStatus) { this.processAuthCallback({ state, user_status: userStatus, user_token: userToken, expire_time: expireTime }); }
    },
    async processAuthCallback(authData) {
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_management.php`, { action: 'handle_oauth_callback', ...authData });
        if (response.data.success) { this.$message.success('令牌授权成功'); this.fetchAccountList(); window.history.replaceState({}, document.title, window.location.pathname); }
        else { this.$message.error(response.data.error || '令牌授权处理失败'); }
      } catch (error) { console.error('处理授权回调失败:', error); this.$message.error('处理授权回调失败'); }
    },
    openOnlineAuthDialog() { this.onlineAuthDialogVisible = true; this.authType = 'ACCOUNT_TYPE_WECHAT'; this.authLoading = false; },
    async startOnlineAuth() {
      this.authLoading = true;
      try {
        const initialCount = this.accountList.length;
        const authParams = { store_id: this.userInfo.id, base_url: config.BASE_URL, auth_type: this.authType, timestamp: Date.now(), initial_count: initialCount };
        const state = encodeURIComponent(JSON.stringify(authParams));
        const authUrl = `https://hd.aijzdi.com/?state=${state}`;
        const authWindow = window.open(authUrl, 'tencent_oauth', 'width=800,height=700');
        if (!authWindow) { this.$message.error('请允许弹出窗口以进行授权'); return; }
        this.checkAuthCompletion(initialCount);
        this.onlineAuthDialogVisible = false;
        this.$message.success('已打开授权页面，请在新窗口中完成授权');
      } catch (error) { console.error('启动授权失败:', error); this.$message.error('启动授权失败'); } finally { this.authLoading = false; }
    },
    async checkAuthCompletion(initialCount) {
      let checkCount = 0, maxCheckCount = 60;
      const checkInterval = setInterval(async () => {
        try {
          checkCount++;
          const authStatus = await this.checkAuthStatusFromBackend(initialCount);
          if (authStatus.status === 'completed') { clearInterval(checkInterval); this.handleAuthSuccess(authStatus); return; }
          else if (authStatus.status === 'failed') { clearInterval(checkInterval); this.$message.error(`授权失败: ${authStatus.message}`); return; }
          if (checkCount >= maxCheckCount) { clearInterval(checkInterval); this.$message.warning('授权检测超时，请检查授权状态'); }
        } catch (error) { console.error('检查授权状态失败:', error); if (checkCount >= 10) { clearInterval(checkInterval); this.$message.error('授权状态检查异常'); } }
      }, 3000);
      setTimeout(() => clearInterval(checkInterval), 600000);
    },
    async checkAuthStatusFromBackend(initialCount) {
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_management.php`, { action: 'check_oauth_status', store_id: this.userInfo.id, initial_count: initialCount });
        return response.data;
      } catch (error) { return { status: 'error', message: '检查失败' }; }
    },
    handleAuthSuccess(authStatus) {
      let msg = '授权成功！';
      if (authStatus.new_accounts?.length > 0) { msg = `授权成功！新账号「${authStatus.new_accounts.map(acc => acc.ghz_name).join('、')}」已创建`; }
      else if (authStatus.updated_accounts?.length > 0) { msg = `授权成功！账号「${authStatus.updated_accounts.map(acc => acc.ghz_name).join('、')}」已更新`; }
      this.$message.success(msg);
      setTimeout(() => { this.fetchAccountList(); this.tryCloseAuthWindow(); }, 1000);
    },
    tryCloseAuthWindow() {
      if (window.opener && !window.opener.closed) window.opener.focus();
      this.$nextTick(() => { this.$notify({ title: '授权完成', message: '账号列表已自动刷新', type: 'success', duration: 3000 }); });
    },
    async checkSmartCardZt() {
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_management.php`, { action: 'check_smart_card_zt', store_id: this.userInfo.id });
        if (response.data.success) { this.hasSmartCardZt = response.data.has_smart_card_zt; }
      } catch (error) { this.hasSmartCardZt = false; }
    },
    async fetchUserList() {
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/business_card_management.php`, { action: 'get_users', store_id: this.userInfo.id });
        if (response.data.data) {
          this.userList = response.data.data;
          this.userMap = {};
          response.data.data.forEach(user => { this.userMap[user.id] = user; });
        }
      } catch (error) { console.error('获取用户列表错误:', error); }
    },
    getUserInfo(userId) {
      const user = this.userMap[userId];
      if (user) return { nickname: user.nickname, phone: user.phone, avatar: user.avatar };
      return { nickname: '管理员', phone: '', avatar: '' };
    },
    async checkAuthorization(row) {
      this.checkLoading = { ...this.checkLoading, [row.id]: true };
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/update_cookies_by_ids.php`, { ids: [row.id] });
        if (response.data.success) {
          const result = response.data.results.find(r => r.id === row.id);
          if (result) {
            if (result.status === 'success') this.$message.success(`ID ${row.id} 授权有效，已更新cookies`);
            else if (result.status === 'cooldown') this.$message.warning(`ID ${row.id} 处于冷却期，${result.next_available}后可更新`);
            else this.$message.warning(`ID ${row.id} ${result.message}`);
          }
        } else { this.$message.error(response.data.error || '检测授权失败'); }
      } catch (error) {
        let errorMsg = '检测授权失败';
        if (error.response?.data?.error) errorMsg = error.response.data.error;
        this.$message.error(errorMsg);
      } finally { this.checkLoading = { ...this.checkLoading, [row.id]: false }; this.fetchAccountList(); }
    },
    async batchCheckAuthorization() {
      const validIds = this.accountList.filter(item => item.cookies && item.cookies !== 'null' && item.cookies !== '[]').map(item => item.id);
      if (validIds.length === 0) { this.$message.warning('没有可检测的授权账号'); return; }
      this.$confirm(`确定要检测${validIds.length}个账号的授权状态吗？`, '批量检测', { confirmButtonText: '确定', cancelButtonText: '取消' }).then(async () => {
        this.batchCheckLoading = true;
        try {
          const response = await axios.post(`${config.BASE_URL}/shop/api/update_cookies_by_ids.php`, { ids: validIds });
          if (response.data.success) {
            const stats = { success: 0, cooldown: 0, error: 0 };
            response.data.results.forEach(r => { if (r.status === 'success') stats.success++; else if (r.status === 'cooldown') stats.cooldown++; else stats.error++; });
            this.$notify({ title: '批量检测完成', message: `成功: ${stats.success}个, 冷却中: ${stats.cooldown}个, 失败: ${stats.error}个`, type: 'success', duration: 5000 });
          } else { this.$message.error(response.data.error || '批量检测失败'); }
        } catch (error) { this.$message.error('批量检测失败'); } finally { this.batchCheckLoading = false; this.fetchAccountList(); }
      }).catch(() => { });
    },
    async startAuthorization(row) {
      this.currentAuthRow = row;
      this.qrDialogVisible = true;
      this.qrLoading = true;
      this.qrCodeBase64 = '';
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_cookies.php`, { action: 'get_qr', id: this.currentAuthRow.id, store_id: this.userInfo.id });
        if (response.data.status === 'success') { this.qrCodeBase64 = response.data.base64; this.webId = response.data.web_id; this.fingerprint = response.data.fingerprint; }
        else { this.$message.error(response.data.error || '获取二维码失败'); }
      } catch (error) { this.$message.error('获取二维码失败'); } finally { this.qrLoading = false; }
    },
    async confirmAuthorization() {
      this.$confirm('确认已完成扫码授权吗？', '确认授权', { confirmButtonText: '确认', cancelButtonText: '取消', type: 'warning' }).then(async () => {
        this.confirmLoading = true;
        try {
          const response = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_cookies.php`, { action: 'update_cookies', web_id: this.webId, fingerprint: this.fingerprint, id: this.currentAuthRow.id, store_id: this.userInfo.id });
          if (response.data.status === 'success') { this.$message.success('授权成功'); this.qrDialogVisible = false; this.fetchAccountList(); }
          else { this.$message.error(response.data.error || '授权失败'); }
        } catch (error) { this.$message.error('授权失败'); } finally { this.confirmLoading = false; }
      }).catch(() => { });
    },
    async fetchAccountList() {
      this.loading = true;
      try {
        const params = { action: this.searchForm.ghz_name ? 'search' : 'list', store_id: this.userInfo.id, page: this.pagination.page, page_size: this.pagination.pageSize };
        if (this.searchForm.ghz_name) params.ghz_name = this.searchForm.ghz_name;
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_management.php`, params);
        if (data.data) {
          const currentTime = Math.floor(Date.now() / 1000);
          this.accountList = data.data.map(item => {
            let expire_in_seconds = 0;
            if (item.expires_in && item.last_refresh_time) {
              const expireTime = item.last_refresh_time + item.expires_in;
              expire_in_seconds = Math.max(0, expireTime - currentTime);
            }
            return { ...item, expire_in_seconds };
          });
          this.pagination.total = data.pagination?.total || data.data.length;
        }
      } catch (error) { this.$message.error('获取账号列表失败'); } finally { this.loading = false; }
    },
    async handleSearch() { this.pagination.page = 1; this.fetchAccountList(); },
    async fetchLicenseInfo() {
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_management.php`, { action: 'license_info', store_id: this.userInfo.id });
        if (response.data && !response.data.error) { this.licenseInfo = response.data; }
      } catch (error) { console.error('获取授权信息失败:', error); }
    },
    async openAddDialog() {
      if (!this.hasSmartCardZt) {
        if (this.accountList.filter(item => item.store_id === this.userInfo.id).length >= 1) { this.$message.warning('单主体模式下只能添加1个账号，请删除现有账号后再添加新账号！'); return; }
      } else {
        if (this.licenseInfo.total !== -1 && this.licenseInfo.remaining <= 0) { this.$message.warning('授权账号数已满，请联系管理员充值！'); return; }
      }
      this.dialogTitle = '新增TX广告';
      this.isEditMode = false;
      this.accountForm = { id: null, ghz_name: '', ip_port: '', ip_password: '', access_token: '', refresh_token: '', notes: '', oauth_type: 'ACCOUNT_TYPE_WECHAT', account_id: '' };
      this.dialogVisible = true;
    },
    openEditDialog(row) {
      this.dialogTitle = '编辑TX广告';
      this.isEditMode = true;
      this.accountForm = {
        id: row.id, ghz_name: row.ghz_name, ip_port: row.ip_port, ip_password: row.ip_password,
        access_token: row.access_token, refresh_token: row.refresh_token, notes: row.notes,
        oauth_type: row.oauth_type || 'ACCOUNT_TYPE_WECHAT', account_id: row.account_id || ''
      };
      this.dialogVisible = true;
    },
    async submitAccountForm() {
      this.$refs.accountForm.validate(async (valid) => {
        if (valid) {
          try {
            if (!this.isEditMode) {
              if (!this.hasSmartCardZt) {
                if (this.accountList.filter(item => item.store_id === this.userInfo.id).length >= 1) { this.$message.warning('单主体模式下只能添加1个账号！'); return; }
              } else {
                const canAdd = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_management.php`, { action: 'can_add', store_id: this.userInfo.id });
                if (canAdd.data.error) { if (canAdd.data.code === 'QUOTA_EXCEEDED') { this.$message.warning('授权账号数已满，请联系管理员充值！'); return; } this.$message.error(canAdd.data.error || '操作失败'); return; }
                if (!canAdd.data.can_add) { this.$message.warning('授权账号数已满，请联系管理员充值！'); return; }
              }
            }
            const action = this.isEditMode ? 'update' : 'add';
            const payload = { ...this.accountForm, store_id: this.userInfo.id, auth_method: 1 };
            const response = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_management.php`, { action, ...payload });
            if (response.data.error) { this.$message.error(response.data.error); }
            else if (response.data.success) { this.$message.success(this.isEditMode ? '更新成功' : '添加成功'); this.dialogVisible = false; this.fetchAccountList(); }
            else { this.$message.error('操作失败，未知错误'); }
          } catch (error) { this.$message.error('操作失败'); }
        }
      });
    },
    async handleDelete(id) {
      this.$confirm('确定要删除该TX广告账号，这个账号绑定的所有数据将会被删除，确定删除吗？', '警告', { confirmButtonText: '确认删除', cancelButtonText: '取消', type: 'warning' }).then(async () => {
        try {
          const response = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_management.php`, { action: 'delete', id: id, store_id: this.userInfo.id });
          if (response.data.error) { this.$message.error(response.data.error); }
          else if (response.data.success) { this.$message.success('删除成功'); this.fetchAccountList(); }
          else { this.$message.error('删除失败，未知错误'); }
        } catch (error) { this.$message.error('删除失败'); }
      }).catch(() => { });
    },
    async refreshToken(row) {
      this.refreshLoading = { ...this.refreshLoading, [row.id]: true };
      try {
        const response = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_management.php`, { action: 'refresh_token', id: row.id, store_id: this.userInfo.id });
        if (response.data.success) { this.$message.success('Token刷新成功'); this.fetchAccountList(); }
        else { this.$message.error(response.data.message || '刷新失败'); }
      } catch (error) { this.$message.error('刷新Token失败'); }
      finally { this.refreshLoading = { ...this.refreshLoading, [row.id]: false }; }
    }
  }
}
</script>

<style scoped lang="scss">
.page-container {
  padding: 20px;
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.license-info {
  display: flex;
  gap: 20px;
  background: #f0f7ff;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
}

.license-item {
  display: flex;
  align-items: center;

  .label {
    color: #606266;
    font-weight: 500;
  }

  .value {
    font-weight: 600;
    color: #409eff;
  }
}

.settings-form {
  max-width: 95%;
  margin: 0 auto;
  padding: 32px 40px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.08);
}

.page-title {
  font-size: 24px;
  color: #1f2937;
  margin-bottom: 32px;
  font-weight: 600;
  text-align: center;
}

.search-bar {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

.el-table {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;

  :deep(.el-table__header) th {
    background-color: #f5f7fa;
    color: #606266;
    font-weight: 600;
  }

  :deep(.el-table__body) tr:hover>td {
    background-color: #f0f7ff !important;
  }
}

.el-button {
  display: inline-flex;
  align-items: center;

  .el-icon {
    margin-right: 5px;
  }
}

:deep(.el-dialog) {
  border-radius: 12px;

  .el-dialog__header {
    padding: 20px 24px;
    border-bottom: 1px solid #ebeef5;

    .el-dialog__title {
      font-weight: 600;
    }
  }

  .el-dialog__body {
    padding: 24px;
  }

  .el-dialog__footer {
    padding: 16px 24px;
    border-top: 1px solid #ebeef5;
  }
}

.password-preview {
  font-family: monospace;
  background: #f5f7fa;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 6px;
}

.empty-value {
  color: #909399;
  font-style: italic;
}

.qr-code-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;

  .loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 200px;

    span {
      margin-top: 10px;
    }
  }

  .qr-code-image {
    width: 200px;
    height: 200px;
    margin-bottom: 20px;
    border: 1px solid #eee;
  }

  .qr-instructions {
    margin-top: 15px;
    text-align: center;
    color: #606266;
    font-size: 14px;

    p {
      margin: 5px 0;
    }
  }
}

.rpa-auth-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.fingerprint-status {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
}

.success-icon,
.error-icon {
  cursor: pointer;
}

:deep(.fingerprint-tooltip) {
  max-width: 400px !important;
}

.fingerprint-detail {
  padding: 10px;

  h4 {
    margin: 0 0 12px 0;
    color: #409EFF;
    font-size: 14px;
    border-bottom: 1px solid #ebeef5;
    padding-bottom: 8px;
  }

  .fingerprint-item {
    display: flex;
    margin-bottom: 8px;
    line-height: 1.4;
  }

  .fingerprint-label {
    font-weight: 600;
    color: #606266;
    min-width: 120px;
    margin-right: 10px;
  }

  .fingerprint-value {
    color: #909399;
    word-break: break-all;
    flex: 1;
  }
}

.online-auth-dialog-wrapper {
  :deep(.el-dialog__header) {
    background: linear-gradient(135deg, #409EFF 0%, #337ecc 100%);
    margin: 0;

    .el-dialog__title {
      color: white;
      font-size: 18px;
      font-weight: 600;
    }

    .el-dialog__headerbtn .el-dialog__close {
      color: white;
    }
  }

  :deep(.el-dialog__body) {
    padding: 30px;
  }
}

.online-auth-dialog {
  .section-title {
    color: #1f2937;
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 20px;
    display: flex;
    align-items: center;

    &::before {
      content: '';
      display: inline-block;
      width: 4px;
      height: 16px;
      background: linear-gradient(135deg, #409EFF 0%, #337ecc 100%);
      border-radius: 2px;
      margin-right: 8px;
    }
  }
}

.auth-type-buttons {
  :deep(.el-radio-group) {
    width: 100%;
    display: flex;
    gap: 16px;
  }

  :deep(.el-radio) {
    flex: 1;
    margin: 0;
    height: auto;

    .el-radio__label {
      padding: 0;
      width: 100%;
    }
  }
}

.auth-type-item {
  border: 2px solid #e8e8e8;
  border-radius: 12px;
  padding: 24px 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  background: white;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: #409EFF;
    transform: translateY(-2px);
    box-shadow: 0 4px 20px rgba(64, 158, 255, 0.15);

    &::before {
      background: #409EFF;
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: transparent;
    transition: all 0.3s ease;
  }
}

:deep(.el-radio.is-checked) .auth-type-item {
  border-color: #409EFF;
  background: linear-gradient(135deg, #f0f7ff 0%, #ecf5ff 100%);

  &::before {
    background: #409EFF;
  }

  .selected-indicator {
    opacity: 1;
    transform: scale(1);
  }
}

.wechat-item:deep(.el-radio.is-checked) .auth-type-item {
  border-color: #07C160;
  background: linear-gradient(135deg, #f0f9f4 0%, #ecf8f1 100%);

  &::before {
    background: #07C160;
  }
}

.auth-icon-wrapper {
  position: relative;
  display: inline-block;
  margin-bottom: 16px;
}

.auth-icon {
  width: 60px;
  height: 60px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  transition: all 0.3s ease;
}

.wechat-icon {
  background: linear-gradient(135deg, #07C160 0%, #06AD56 100%);
  color: white;
}

.qq-icon {
  background: linear-gradient(135deg, #12B7F5 0%, #0EA5E0 100%);
  color: white;
}

.selected-indicator {
  position: absolute;
  top: -4px;
  right: -4px;
  width: 20px;
  height: 20px;
  background: #07C160;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transform: scale(0);
  transition: all 0.3s ease;

  .el-icon {
    color: white;
    font-size: 12px;
  }
}

.auth-text {
  text-align: left;
}

.auth-title {
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 6px;
}

.auth-desc {
  font-size: 13px;
  color: #666;
  margin-bottom: 12px;
  line-height: 1.4;
}

.auth-features {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.feature-tag {
  background: rgba(64, 158, 255, 0.1);
  color: #409EFF;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
}

.wechat-item .feature-tag {
  background: rgba(7, 193, 96, 0.1);
  color: #07C160;
}

.auth-process-section {
  margin: 30px 0;
  padding: 25px;
  background: #f8fafc;
  border-radius: 12px;
}

.process-steps {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.process-step {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
}

.step-number {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #409EFF 0%, #337ecc 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
}

.step-content {
  flex: 1;
}

.step-title {
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
}

.step-desc {
  font-size: 12px;
  color: #666;
  line-height: 1.4;
}

.step-arrow {
  color: #909399;
  font-size: 18px;
  font-weight: 300;
  flex-shrink: 0;
}

.tips-section {
  background: linear-gradient(135deg, #fffbf0 0%, #fef6e6 100%);
  border: 1px solid #ffe58f;
  border-radius: 8px;
  padding: 20px;
}

.tips-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;

  .tips-icon {
    color: #faad14;
    font-size: 16px;
  }

  span {
    color: #1f2937;
    font-weight: 600;
    font-size: 14px;
  }
}

.tips-content p {
  margin: 6px 0;
  color: #666;
  font-size: 13px;
  line-height: 1.5;
}

.dialog-footer {
  display: flex;
  gap: 12px;
  justify-content: flex-end;

  .cancel-btn {
    padding: 12px 24px;
    border: 1px solid #dcdfe6;

    &:hover {
      border-color: #409EFF;
      color: #409EFF;
    }
  }

  .confirm-btn {
    padding: 12px 32px;
    background: linear-gradient(135deg, #07C160 0%, #06AD56 100%);
    border: none;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(7, 193, 96, 0.3);
    }

    .el-icon {
      margin-right: 6px;
    }
  }
}

@media (max-width: 768px) {
  .online-auth-dialog-wrapper :deep(.el-dialog) {
    width: 95% !important;
    margin: 20px auto;
  }

  .auth-type-buttons :deep(.el-radio-group) {
    flex-direction: column;
  }

  .process-steps {
    flex-direction: column;
    gap: 20px;
  }

  .step-arrow {
    transform: rotate(90deg);
  }
}

:deep(.el-table .newly-authorized) {
  background-color: #f0f9ff !important;
  animation: highlightFade 3s ease-in-out;
}

:deep(.el-table .newly-authorized:hover) {
  background-color: #e6f7ff !important;
}

@keyframes highlightFade {
  0% {
    background-color: #bae7ff;
  }

  50% {
    background-color: #91d5ff;
  }

  100% {
    background-color: #f0f9ff;
  }
}

:deep(.el-table .updated-account) {
  background-color: #f6ffed !important;
  animation: updateHighlight 3s ease-in-out;
}

@keyframes updateHighlight {
  0% {
    background-color: #d9f7be;
  }

  50% {
    background-color: #b7eb8f;
  }

  100% {
    background-color: #f6ffed;
  }
}

.token-auth-status {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 32px;
}

.token-auth-success,
.token-auth-warning,
.token-auth-error {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.token-auth-success {
  background-color: #f0f9ff;
  border: 1px solid #91d5ff;
  color: #67C23A;

  &:hover {
    background-color: #e6f7ff;
    transform: translateY(-1px);
  }
}

.token-auth-warning {
  background-color: #fdf6ec;
  border: 1px solid #f5dab1;
  color: #E6A23C;
}

.token-auth-error {
  background-color: #fef0f0;
  border: 1px solid #fbc4c4;
  color: #F56C6C;
}

.token-status-text {
  font-size: 12px;
  font-weight: 500;
}

.token-auth-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
  }
}

.token-expire-info {
  font-size: 12px;

  .refresh-time {
    color: #909399;
  }
}

.user-info {
  .user-nickname {
    font-weight: 500;
    margin-bottom: 4px;
  }

  .user-phone {
    color: #909399;
    font-size: 12px;
  }
}

@keyframes pulse {
  0% {
    background-color: #f0f9ff;
  }

  50% {
    background-color: #fdf6ec;
  }

  100% {
    background-color: #f0f9ff;
  }
}
</style>